/**
 * Channel icon — small inline SVG glyph per messaging medium. Used in
 * inbox rows + conversation header. Sized to fit alongside small text
 * (default 14px); accepts `className` for size/color overrides.
 *
 * Keep these as inline SVGs (no icon library) — bundle stays small and we
 * avoid coupling channel UI to a third-party iconset.
 *
 * Channel ids match `messaging-gateway/src/modules/channels/types/channel.ts`
 * (ChannelType: 'messenger' | 'instagram' | 'telegram' | 'email' | 'custom').
 *
 * **Extension point — adding a new channel:**
 *   1. Add the channel id to the gateway `ChannelType` enum.
 *   2. Add a `case 'newchannel':` branch below with its glyph + brand color.
 *   3. Unknown channel ids fall through to the generic chat-bubble (`custom`)
 *      branch — so the inbox renders correctly *before* the icon ships,
 *      which lets the data-side work land first.
 *
 *   `'web'` (live-chat widget on customer's site) is **deferred** — the
 *   data model + inbox UI already support it (channel is opaque metadata
 *   everywhere except this file); when it ships, drop in a globe / browser
 *   glyph here. Until then, `'web'` rows show the `custom` fallback icon.
 */
interface Props {
  channel: string;
  className?: string;
}

export function ChannelIcon({ channel, className = 'w-3.5 h-3.5' }: Props) {
  switch (channel) {
    case 'messenger':
      return (
        <svg
          viewBox="0 0 24 24"
          aria-label="Messenger"
          className={`${className} text-blue-500`}
          fill="currentColor"
        >
          <path d="M12 2C6.5 2 2 6.1 2 11.2c0 2.8 1.4 5.3 3.6 7v3.3l3.3-1.8c1 .3 2 .4 3.1.4 5.5 0 10-4.1 10-9.2S17.5 2 12 2zm1.1 12.2l-2.5-2.7-4.9 2.7 5.4-5.7 2.6 2.6 4.8-2.6-5.4 5.7z" />
        </svg>
      );
    case 'instagram':
      return (
        <svg
          viewBox="0 0 24 24"
          aria-label="Instagram"
          className={`${className} text-pink-500`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
        >
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'telegram':
      return (
        <svg
          viewBox="0 0 24 24"
          aria-label="Telegram"
          className={`${className} text-sky-500`}
          fill="currentColor"
        >
          <path d="M21.8 4.3l-3 14.2c-.2 1-.8 1.3-1.7.8L12 16l-2.5 2.4c-.3.3-.5.5-1 .5l.4-5 9.2-8.3c.4-.4-.1-.6-.6-.2L6.2 12.4l-4.9-1.5c-1.1-.3-1.1-1 .2-1.5L20.2 3c.9-.3 1.7.2 1.6 1.3z" />
        </svg>
      );
    case 'email':
      return (
        <svg
          viewBox="0 0 24 24"
          aria-label="Email"
          className={`${className} text-gray-500`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
        >
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
      );
    case 'custom':
    default:
      // Generic chat bubble for unknown / custom channels (e.g. web widget).
      return (
        <svg
          viewBox="0 0 24 24"
          aria-label={channel}
          className={`${className} text-gray-400`}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.8}
        >
          <path d="M21 12c0 4.4-4 8-9 8a9.9 9.9 0 01-4.3-.9L3 20l1.4-3.7A8.4 8.4 0 013 12c0-4.4 4-8 9-8s9 3.6 9 8z" />
        </svg>
      );
  }
}
